import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop } from "../../utils";
import { normalizePrice } from "../../../../utils/priceUtils.js";

import OrderLine from "./OrderLine";
import OTOOrdersInteractive from "../OTOOrdersInteractive";
import { getOtoLegKind } from "../../../../utils/orderConversions.js";

class OrderInteractiveModular extends Component {
  constructor(props) {
    super(props);

    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleTrashClick = this.handleTrashClick.bind(this);
    this.handleSendClick = this.handleSendClick.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);

    this.state = {
      dragStartPosition: null,
    };
  }

  handleDragStart(moreProps) {
    const { order } = this.props;
    const { price } = order;
    const { mouseXY } = moreProps;
    const {
      chartConfig: { yScale },
    } = moreProps;
    const [, mouseY] = mouseXY;

    const dy = mouseY - yScale(price);

    this.setState({
      dragStartPosition: { price, dy },
    });

    // Call the original onDragStart if provided
    if (this.props.onDragStart) {
      this.props.onDragStart(moreProps);
    }
  }

  handleDrag(moreProps) {
    const { onDrag, order } = this.props;
    const { dragStartPosition } = this.state;

    // Check if moreProps is defined
    if (!moreProps || !moreProps.mouseXY || !moreProps.chartConfig) {
      console.warn(
        "OrderInteractiveModular.handleDrag: moreProps is invalid",
        moreProps,
      );
      return;
    }

    if (!dragStartPosition) {
      console.warn(
        "OrderInteractiveModular.handleDrag: dragStartPosition is invalid",
        dragStartPosition,
      );
      return;
    }

    const {
      mouseXY: [, mouseY],
      chartConfig: { yScale },
    } = moreProps;

    const { dy } = dragStartPosition;
    const newPrice = yScale.invert(mouseY - dy);

    // Apply mintick normalization using the centralized utility
    const normalizedPrice = normalizePrice(order.symbol, newPrice);

    // Call the original onDrag callback with the normalized price
    if (onDrag) {
      onDrag(normalizedPrice);
    }
  }

  handleTrashClick() {
    console.log("🗑️ OrderInteractiveModular: handleTrashClick called");
    const { index, onDelete } = this.props;
    if (onDelete) {
      onDelete(index);
    }
  }

  handleSendClick() {
    console.log(
      "➡️ OrderInteractiveModular: handleSendClick called - changing state to pending",
    );
    const { index, onSubmit, order } = this.props;

    console.log("🔍 OrderInteractiveModular props check:", {
      index,
      order: order ? { id: order.id, state: order.state } : null,
      hasOnSubmit: !!onSubmit,
    });

    if (order.state === "local") {
      console.log("✅ Order state is local, proceeding with submission");
      if (onSubmit) {
        console.log("🎯 Using onSubmit handler");
        onSubmit(index);
      } else {
        console.error("❌ No onSubmit handler available!");
      }
    } else {
      console.log("❌ Order state is not local:", order.state);
    }
  }

  handleDoubleClick(moreProps) {
    // Call OrderLine's handleDoubleClick method
    if (this.orderLineRef && this.orderLineRef.handleDoubleClick) {
      this.orderLineRef.handleDoubleClick(moreProps);
    }
  }

  render() {
    const {
      order,
      selected,
      hovering,
      onDragComplete,
      onHover,
      onUnHover,
      offset = 0,
      allOrders = [],
    } = this.props;

    return (
      <g onDoubleClick={this.handleDoubleClick}>
        {/* Single unified component that handles everything */}
        <OrderLine
          ref={(ref) => {
            this.orderLineRef = ref;
          }}
          order={order}
          selected={selected}
          hovering={hovering}
          offset={offset}
          allOrders={allOrders}
          onDragStart={this.handleDragStart}
          onDrag={this.handleDrag}
          onDragComplete={onDragComplete}
          onHover={onHover}
          onUnHover={onUnHover}
          onTrashClick={this.handleTrashClick}
          onSendClick={this.handleSendClick}
          onStopsClick={this.props.onStopsClick}
          onDoubleClick={this.handleDoubleClick}
          onQuantityEdit={this.props.onQuantityEdit}
        />

        {/* Render OTO orders with drag support */}
        {order.oto &&
          order.oto.length > 0 &&
          (() => {
            // Classify without mutating legs or injecting transient type field
            // Also deduplicate by ID to prevent duplicate key warnings
            const stopOrders = [];
            const profitOrders = [];
            const seenIds = new Set();
            for (const leg of order.oto) {
              // Skip duplicates
              if (seenIds.has(leg.id)) {
                continue;
              }
              seenIds.add(leg.id);

              // Prefer explicit leg.type if present (backward compat), else derive
              let kind = leg.type;
              if (!kind) {
                kind = getOtoLegKind(leg);
                if (!kind) {
                  // Fallback heuristic: STOPLIMIT => stop, others => profit
                  kind = leg.orderType === "STOPLIMIT" ? "stop" : "profit";
                }
              }
              (kind === "stop" ? stopOrders : profitOrders).push(leg);
            }

            return (
              <OTOOrdersInteractive
                mainOrder={order}
                stopOrders={stopOrders}
                profitOrders={profitOrders}
                allOrders={allOrders}
                tempOTOPrices={this.props.tempOTOPrices}
                onUpdateOTOPrice={this.props.onOTOEdit}
                onRemoveOTOOrder={(...args) => {
                  if (!this.props.onOTORemove) return;
                  if (args.length === 0) return;
                  if (args.length === 2) {
                    return this.props.onOTORemove(args[0], args[1]);
                  }
                  return this.props.onOTORemove(order.id, args[0]);
                }}
                onDragStateChange={this.props.onDragStateChange}
              />
            );
          })()}
      </g>
    );
  }
}

OrderInteractiveModular.propTypes = {
  index: PropTypes.number,
  order: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.oneOf([
      "local",
      "pending",
      "open",
      "filled",
      "cancelled",
      "rejected",
    ]).isRequired,
    side: PropTypes.oneOf(["buy", "sell"]).isRequired,
    price: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
    symbol: PropTypes.string.isRequired,
    oto: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        quantity: PropTypes.number.isRequired,
        symbol: PropTypes.string.isRequired,
        side: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,

  onDrag: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragComplete: PropTypes.func,
  onDragStateChange: PropTypes.func,
  onDelete: PropTypes.func,
  onSubmit: PropTypes.func,
  onQuantityEdit: PropTypes.func,
  onStopsClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,

  // OTO event handlers
  onOTOEdit: PropTypes.func,
  onOTORemove: PropTypes.func,
  tempOTOPrices: PropTypes.object, // Map containing temporary OTO prices during drag

  selected: PropTypes.bool,
  hovering: PropTypes.bool,
  offset: PropTypes.number,
  allOrders: PropTypes.array,
};

OrderInteractiveModular.defaultProps = {
  onDrag: noop,
  onDragStart: noop,
  onDragComplete: noop,
  onDragStateChange: noop,
  onDelete: noop,
  onSubmit: noop,
  onQuantityEdit: noop,
  onStopsClick: noop,
  onDoubleClick: noop,
  onHover: noop,
  onUnHover: noop,
  onOTOEdit: noop,
  onOTORemove: noop,
  tempOTOPrices: null,
  selected: false,
  hovering: false,
};

export default OrderInteractiveModular;
